import React, { useEffect, useState, useMemo, useCallback } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { apiService } from '../services/apiService';
import { useToast } from '../components/Toast';
import { useAuth } from '../services/authContext';
import SetupGuard from '../components/SetupGuard';

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const DAY_COLORS = { Monday:'#1976D2', Tuesday:'#388E3C', Wednesday:'#F57C00', Thursday:'#7B1FA2', Friday:'#D32F2F', Saturday:'#546E7A' };
const emptyForm = { classId:'', moduleId:'', teacherId:'', dayOfWeek:'Monday', startTime:'08:00', endTime:'09:00', room:'' };

const Timetable = () => {
  const { user }                        = useAuth();
  const { toast }                       = useToast();
  const isAdmin                         = user?.role === 'admin';
  const isTeacher                       = user?.role === 'teacher';
  const [entries, setEntries]           = useState([]);
  const [classes, setClasses]           = useState([]);
  const [modules, setModules]           = useState([]);
  const [teachers, setTeachers]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [filterClass, setFilterClass]   = useState('');
  const [isModalOpen, setModal]         = useState(false);
  const [current, setCurrent]           = useState(null);
  const [form, setForm]                 = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [tt, cls, mods, tchs] = await Promise.all([
        apiService.getAll('timetable'),
        apiService.getAll('classes'),
        apiService.getAll('modules'),
        apiService.getAll('teachers'),
      ]);
      setEntries(tt   || []);
      setClasses(cls  || []);
      setModules(mods || []);
      setTeachers(tchs || []);
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to load timetable.', 'error');
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() =>
    entries.filter(e => filterClass ? String(e.classId) === filterClass : true),
    [entries, filterClass]);

  // Group by day
  const byDay = useMemo(() =>
    DAYS.reduce((acc, day) => {
      acc[day] = filtered.filter(e => e.dayOfWeek === day).sort((a, b) => a.startTime.localeCompare(b.startTime));
      return acc;
    }, {}), [filtered]);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const openEdit = entry => {
    setCurrent(entry);
    setForm({ classId: entry.classId, moduleId: entry.moduleId || entry.subjectId, teacherId: entry.teacherId || '', dayOfWeek: entry.dayOfWeek, startTime: entry.startTime, endTime: entry.endTime, room: entry.room || '' });
    setModal(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.startTime >= form.endTime) { toast('End time must be after start time.', 'warning'); return; }
    try {
      setSaving(true);
      if (current) {
        await apiService.update('timetable', current.id, form);
        toast('Schedule updated.', 'success');
      } else {
        await apiService.create('timetable', form);
        toast('Schedule entry added.', 'success');
      }
      setModal(false); setCurrent(null); setForm(emptyForm);
      await load();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to save.', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Remove this schedule entry?')) return;
    try {
      await apiService.delete('timetable', id);
      toast('Entry removed.', 'success');
      await load();
    } catch (err) { toast(err.response?.data?.message || 'Failed to delete.', 'error'); }
  };

  if (loading) return <LoadingSpinner message="Loading timetable..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{isAdmin ? 'Timetable Management' : 'My Timetable'}</h1>
        {isAdmin && <button className="btn btn-primary" onClick={() => { setCurrent(null); setForm(emptyForm); setModal(true); }}>+ Add Entry</button>}
      </div>

      {isAdmin ? (
        <SetupGuard checks={[
          {
            ok: classes.length > 0,
            icon: '🏫',
            label: 'No Classes exist yet',
            description: 'Create a class or batch before building a timetable.',
            action: { label: 'Create Class', to: '/programmes' },
          },
          {
            ok: modules.length > 0,
            icon: '📚',
            label: 'No Modules exist yet',
            description: 'Create modules before scheduling them in the timetable.',
            action: { label: 'Create Module', to: '/modules' },
          },
          {
            ok: teachers.length > 0,
            icon: '👨🏫',
            label: 'No Lecturers exist yet',
            description: 'Add lecturers before assigning them to timetable slots.',
            action: { label: 'Add Lecturer', to: '/lecturers' },
          },
        ]}>

        <div style={{ marginBottom: '1.5rem', maxWidth: 280 }}>
          <select value={filterClass} onChange={e => setFilterClass(e.target.value)}>
            <option value="">All Classes</option>
            {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>

        {DAYS.map(day => (
          byDay[day].length === 0 ? null : (
            <div key={day} style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ color: DAY_COLORS[day], borderBottom: `2px solid ${DAY_COLORS[day]}`, paddingBottom: '0.4rem', marginBottom: '0.75rem' }}>{day}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                {byDay[day].map(e => (
                  <div key={e.id} style={{ background: 'var(--card-bg)', border: `1px solid ${DAY_COLORS[day]}44`, borderLeft: `4px solid ${DAY_COLORS[day]}`, borderRadius: 8, padding: '0.9rem 1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '1rem' }}>{e.moduleCode || e.subjectCode} — {e.moduleName || e.subjectName}</div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-light)', margin: '0.2rem 0' }}>{e.className}</div>
                        <div style={{ fontSize: '0.85rem' }}>🕐 {e.startTime} – {e.endTime}</div>
                        {e.teacherName && <div style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>👨🏫 {e.teacherName}</div>}
                        {e.room && <div style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>📍 {e.room}</div>}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                        <button className="btn-icon" onClick={() => openEdit(e)} style={{ fontSize: '0.78rem' }}>Edit</button>
                        <button className="btn-icon" onClick={() => handleDelete(e.id)} style={{ fontSize: '0.78rem', color: '#c62828' }}>Remove</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ))}

        {filtered.length === 0 && <div className="no-data-message">No timetable entries found. Add one to get started.</div>}
        </SetupGuard>
      ) : (
        <>
          {DAYS.map(day => (
            byDay[day].length === 0 ? null : (
              <div key={day} style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ color: DAY_COLORS[day], borderBottom: `2px solid ${DAY_COLORS[day]}`, paddingBottom: '0.4rem', marginBottom: '0.75rem' }}>{day}</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                  {byDay[day].map(e => (
                    <div key={e.id} style={{ background: 'var(--card-bg)', border: `1px solid ${DAY_COLORS[day]}44`, borderLeft: `4px solid ${DAY_COLORS[day]}`, borderRadius: 8, padding: '0.9rem 1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '1rem' }}>{e.moduleCode || e.subjectCode} — {e.moduleName || e.subjectName}</div>
                          <div style={{ fontSize: '0.82rem', color: 'var(--text-light)', margin: '0.2rem 0' }}>{e.className}</div>
                          <div style={{ fontSize: '0.85rem' }}>🕐 {e.startTime} – {e.endTime}</div>
                          {!isTeacher && e.teacherName && <div style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>👨🏫 {e.teacherName}</div>}
                          {e.room && <div style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>📍 {e.room}</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
          {filtered.length === 0 && (
            <div className="no-data-message" style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📅</div>
              <h3>Your timetable is empty</h3>
              <p>No classes have been scheduled for you yet. Please check back later or contact the administration.</p>
            </div>
          )}
        </>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setModal(false)} title={current ? 'Edit Schedule Entry' : 'Add Schedule Entry'}>
        <form onSubmit={handleSubmit} className="form">
          <div className="form-row">
            <div className="form-group">
              <label>Class *</label>
              <select name="classId" value={form.classId} onChange={handleChange} required disabled={!!current}>
                <option value="">Select Class</option>
                {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Module *</label>
              <select name="moduleId" value={form.moduleId} onChange={handleChange} required>
                <option value="">Select Module</option>
                {modules.map(m => <option key={m._id} value={m._id}>{m.code} — {m.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Day *</label>
              <select name="dayOfWeek" value={form.dayOfWeek} onChange={handleChange} required>
                {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Lecturer</label>
              <select name="teacherId" value={form.teacherId} onChange={handleChange}>
                <option value="">Select Lecturer</option>
                {teachers.map(t => <option key={t._id} value={t._id}>{t.firstName} {t.lastName}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Time *</label>
              <input type="time" name="startTime" value={form.startTime} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>End Time *</label>
              <input type="time" name="endTime" value={form.endTime} onChange={handleChange} required />
            </div>
          </div>
          <div className="form-group">
            <label>Room / Venue</label>
            <input name="room" value={form.room} onChange={handleChange} placeholder="e.g. Room 101, Lab A" />
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : current ? 'Update' : 'Add Entry'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Timetable;
